import React, { useMemo } from 'react';
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table';

import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { TablePagination } from '../../../components';
import './styles.scoped.css';

function getSortedIcon(isSorted, isSortedDesc) {
  let iconProp = <FaSort />;
  if (isSorted) {
    iconProp = isSortedDesc ? <FaSortDown /> : <FaSortUp />;
  }
  return iconProp;
}

const columnClassName = 'align-middle text-nowrap';
export const webPortalActivityColumns = [
  {
    Header: 'Employee_ID',
    accessor: 'Employee_ID',
    className: columnClassName,
  },
  {
    Header: 'Empl_Name',
    accessor: 'Empl_Name',
    className: columnClassName,
  },
  {
    Header: 'Customer_ID',
    accessor: 'Customer_ID',
    className: columnClassName,
  },
  {
    Header: 'Customer_Name',
    accessor: 'Customer_Name',
    className: columnClassName,
  },
  {
    Header: 'LOB',
    accessor: 'LOB',
    className: columnClassName,
  },
  {
    Header: 'Job_Id',
    accessor: 'Job_Id',
    className: columnClassName,
  },
  {
    Header: 'Branch',
    accessor: 'Branch',
    className: columnClassName,
  },
  {
    Header: 'Customer Contact Email',
    accessor: 'Customer_Contact_Email',
    className: columnClassName,
  },
  {
    Header: 'Work_Date',
    accessor: 'Work_Date',
    className: columnClassName,
  },
  {
    Header: 'Pay_Code',
    accessor: 'Pay_Code',
    className: columnClassName,
  },
  {
    Header: 'Reg Hours',
    accessor: 'Reg_Hours',
    className: columnClassName,
  },
  {
    Header: 'OT Hours',
    accessor: 'Overtime_Hours',
    className: columnClassName,
  },
  {
    Header: 'DT Hours',
    accessor: 'Doubletime_Hours',
    className: columnClassName,
  },
  {
    Header: 'Hol Hours',
    accessor: 'Holiday_Hours',
    className: columnClassName,
  },
  {
    Header: 'Total_Hours',
    accessor: 'Total_Hours',
    className: columnClassName,
  },
  {
    Header: 'Expense Amount',
    accessor: 'Expense_Amount',
    className: columnClassName,
  },
  {
    Header: 'Submitted_By',
    accessor: 'Submitted_By',
    className: columnClassName,
  },
  {
    Header: 'Back Office Processed',
    accessor: 'BackOffice_Processed',
    className: columnClassName,
  },
  {
    Header: 'Customer_Approved',
    accessor: 'Customer_Approved',
    className: columnClassName,
  },
  {
    Header: 'Customer_Approver',
    accessor: 'Customer_Approver',
    className: columnClassName,
  },
  {
    Header: 'Customer_Approval_Status',
    accessor: 'Customer_Approval_Status',
    className: columnClassName,
  },
  {
    Header: 'Customer_Approved_Date_Time',
    accessor: 'Customer_Approval_Date_Time',
    className: columnClassName,
  },
  {
    Header: 'Minimum_Guarantee_Opt_Out_By',
    accessor: 'Minimum_Guarantee_Opt_Out_By',
    className: columnClassName,
  },
  {
    Header: 'Minimum_Guarantee_Opt_Out_By_Id',
    accessor: 'Minimum_Guarantee_Opt_Out_By_Id',
    className: columnClassName,
  },
  {
    Header: 'Minimum_Guarantee_Opt_Out_On',
    accessor: 'Minimum_Guarantee_Opt_Out_On',
    className: columnClassName,
  },
  {
    Header: 'Minimum_Guarantee_Opt_Out_Note',
    accessor: 'Minimum_Guarantee_Opt_Out_Note',
    className: columnClassName,
  },
  {
    Header: 'Employee_Comments',
    accessor: 'Employee_Comments',
    className: columnClassName,
  },
  {
    Header: 'Customer_Comments',
    accessor: 'Customer_Comments',
    className: columnClassName,
  },
];

const WebPortalActivityTable = ({ data, loading }) => {
  const memoData = useMemo(() => data, [data]);

  const tableInstance = useTable(
    {
      columns: webPortalActivityColumns,
      data: memoData,
      initialState: {
        pageSize: 200,
        sortBy: [
          {
            id: 'CreatedAt',
            desc: true,
          },
        ],
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
  } = tableInstance;

  const tableHeaders = headerGroups.map(headerGroup => {
    const headers = headerGroup.headers.map(column => (
      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
        {column.render('Header')}
        <span>
          {column.canSort
            ? getSortedIcon(column.isSorted, column.isSortedDesc)
            : null}
        </span>
      </th>
    ));
    return <tr {...headerGroup.getHeaderGroupProps()}>{headers}</tr>;
  });

  const tableRows = page.map(row => {
    prepareRow(row);
    const cells = row.cells.map(cell => (
      <td
        {...cell.getCellProps({
          className: cell.column.className,
          style: cell.column.style,
        })}
      >
        {cell.render('Cell')}
      </td>
    ));

    return <tr {...row.getRowProps()}>{cells}</tr>;
  });

  return (
    <Container fluid>
      <Row className="align-items-center">
        <Col className="d-flex justify-content-center justify-content-md-end">
          <TablePagination tableInstance={tableInstance} />
        </Col>
      </Row>
      <Row>
        <Col className="border border-light rounded shadow-sm">
          <Table hover responsive borderless {...getTableProps()}>
            <thead className="text-nowrap">{tableHeaders}</thead>
            <tbody {...getTableBodyProps()}>{tableRows}</tbody>
          </Table>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {loading && <Spinner animation="border" />}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default WebPortalActivityTable;
